import React from 'react';
import {
   Avatar,
   Box,
   Button,
   Dialog,
   Flex,
   Heading,
   RadioGroup,
   Select,
   Skeleton,
   Switch,
   Text,
   TextField,
} from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useDebouncedCallback } from 'use-debounce';

import { AdminSideBar } from 'admin/elements/side-bar';
import APNavBar from 'Components/adminpanel/Elements/APNavBar';
import { AutocompleteV2 } from 'shared/components/autocomplete-v2';
import { DateInput } from 'shared/components';
import { BusyIndicator } from 'shared/components/busy-indicator';
import { FormField } from 'shared/components/form-field';
import { ErrorOutlineIcon, IconWest } from 'shared/icons';
import { useEditCandidateProfile } from './edit-candidate-profile.provider';

import './edit-candidate-profile.scss';

const DEFAULT_AVATAR = '/Images/jobseekerSide/dummy-profile.png';

const EditCandidateProfileView = () => {
   const store = useEditCandidateProfile();
   const navigate = useNavigate();
   const inputId = React.useId();
   const [confirmOpen, setConfirmOpen] = React.useState(false);
   const [photoError, setPhotoError] = React.useState<string | null>(null);
   const [countryQuery, setCountryQuery] = React.useState('');

   const debouncedCities = useDebouncedCallback((q: string) => {
      store.searchCities(q);
   }, 500);

   const countryItems = React.useMemo(() => {
      if (!countryQuery) {
         return store.countries;
      }

      const query = countryQuery.toLowerCase();
      return store.countries.filter((country) => country.name.toLowerCase().includes(query));
   }, [countryQuery, store.countries]);

   React.useEffect(() => {
      store.loadCandidate();
      window.scrollTo(0, 0);
   }, []);

   React.useEffect(() => {
      return () => {
         store.resetSelectedPhoto();
      };
   }, [store]);

   const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
         return;
      }

      const result = await store.selectPhotoFile(file);
      setPhotoError(result.error ?? null);
      e.target.value = '';
   };

   const requestSubmit = () => {
      if (store.validate()) {
         setConfirmOpen(true);
      }
   };

   const renderMessengerRows = () =>
      store.messengerRows.map((row, index) => {
         const isLastRow = index === store.messengerRows.length - 1;
         const availableOptions = store.getMessengerOptions(index);

         return (
            <Flex
               key={`messenger-${index}`}
               className="feature-edit-candidate-profile__contact-row"
               direction={{ initial: 'column', md: 'row' }}
               gap="4"
               align="end"
            >
               <Box className="feature-edit-candidate-profile__contact-type">
                  <FormField label="Месенджер">
                     <Select.Root
                        size="3"
                        value={row.type}
                        onValueChange={(value) => store.updateMessengerRow(index, 'type', value)}
                     >
                        <Select.Trigger color="indigo" placeholder="Месенджер" />
                        <Select.Content position="popper">
                           {availableOptions.map((option) => (
                              <Select.Item key={option} value={option}>
                                 {option}
                              </Select.Item>
                           ))}
                        </Select.Content>
                     </Select.Root>
                  </FormField>
               </Box>

               <Box className="feature-edit-candidate-profile__contact-link">
                  <FormField label={store.getMessengerFieldLabel(row.type)}>
                     <TextField.Root
                        size="3"
                        color="indigo"
                        autoComplete="off"
                        placeholder={store.getMessengerPlaceholder(row.type)}
                        value={row.link}
                        onChange={(e) => store.updateMessengerRow(index, 'link', e.target.value)}
                     />
                  </FormField>
               </Box>

               <Box className="feature-edit-candidate-profile__contact-action">
                  {isLastRow && store.canAddMessengerRow ? (
                     <Button
                        type="button"
                        size="3"
                        variant="soft"
                        color="red"
                        onClick={() => store.addMessengerRow()}
                     >
                        Додати ще
                     </Button>
                  ) : store.messengerRows.length > 1 ? (
                     <Button
                        type="button"
                        size="3"
                        variant="outline"
                        onClick={() => store.removeMessengerRow(index)}
                     >
                        Видалити
                     </Button>
                  ) : null}
               </Box>
            </Flex>
         );
      });

   const renderSocialRows = () =>
      store.socialRows.map((row, index) => {
         const isLastRow = index === store.socialRows.length - 1;
         const availableOptions = store.getSocialOptions(index);

         return (
            <Flex
               key={`social-${index}`}
               className="feature-edit-candidate-profile__contact-row"
               direction={{ initial: 'column', md: 'row' }}
               gap="4"
               align="end"
            >
               <Box className="feature-edit-candidate-profile__contact-type">
                  <FormField label="Соцмережа">
                     <Select.Root
                        size="3"
                        value={row.type}
                        onValueChange={(value) => store.updateSocialRow(index, 'type', value)}
                     >
                        <Select.Trigger color="indigo" placeholder="Соцмережа" />
                        <Select.Content position="popper">
                           {availableOptions.map((option) => (
                              <Select.Item key={option} value={option}>
                                 {option}
                              </Select.Item>
                           ))}
                        </Select.Content>
                     </Select.Root>
                  </FormField>
               </Box>

               <Box className="feature-edit-candidate-profile__contact-link">
                  <FormField label="Посилання на соцмережу">
                     <TextField.Root
                        size="3"
                        color="indigo"
                        autoComplete="off"
                        placeholder="https://"
                        value={row.link}
                        onChange={(e) => store.updateSocialRow(index, 'link', e.target.value)}
                     />
                  </FormField>
               </Box>

               <Box className="feature-edit-candidate-profile__contact-action">
                  {isLastRow && store.canAddSocialRow ? (
                     <Button
                        type="button"
                        size="3"
                        variant="soft"
                        color="red"
                        onClick={() => store.addSocialRow()}
                     >
                        Додати ще
                     </Button>
                  ) : store.socialRows.length > 1 ? (
                     <Button
                        type="button"
                        size="3"
                        variant="outline"
                        onClick={() => store.removeSocialRow(index)}
                     >
                        Видалити
                     </Button>
                  ) : null}
               </Box>
            </Flex>
         );
      });

   return (
      <>
         <APNavBar />
         <Box pt="80px" className="feature-edit-candidate-profile">
            <Flex width="270px" minWidth="270px">
               <AdminSideBar />
            </Flex>

            <Box className="feature-edit-candidate-profile__content">
               <Flex
                  className="feature-edit-candidate-profile__header"
                  justify="between"
                  align="center"
               >
                  <Box>
                     <Flex gap="2" align="center" mb="2">
                        <Button
                           variant="ghost"
                           size="1"
                           onClick={() => navigate('/admin/admins/dashboard')}
                        >
                           Панель керування
                        </Button>
                        <Text color="gray">/</Text>
                        <Button
                           variant="ghost"
                           size="1"
                           onClick={() => navigate('/admin/candidates')}
                        >
                           Кандидати
                        </Button>
                        <Text color="gray">/</Text>
                        <Text size="2" color="gray">
                           Редагування даних кандидата
                        </Text>
                     </Flex>

                     <Heading as="h1" weight="medium">
                        Редагувати кандидата
                     </Heading>
                  </Box>
               </Flex>

               <Box width="100%">
                  {store.isLoading ? (
                     <Box
                        className="feature-edit-candidate-profile__skeleton-wrap"
                        aria-busy="true"
                     >
                        <Skeleton width="100%" height="720px" />
                        <Flex
                           className="feature-edit-candidate-profile__busy-indicator"
                           align="center"
                           justify="center"
                        >
                           <Flex
                              className="feature-edit-candidate-profile__busy-content"
                              align="center"
                              gap="2"
                           >
                              <BusyIndicator />
                              <Text size="3" weight="medium">
                                 Завантаження...
                              </Text>
                           </Flex>
                        </Flex>
                     </Box>
                  ) : (
                     <Box width="100%">
                        <Box mb="6">
                           <Text as="div" size="3" mb="3" weight="medium">
                              Фото профілю
                           </Text>

                           <Flex
                              gap="4"
                              align={{ initial: 'start', sm: 'center' }}
                              direction={{ initial: 'column', sm: 'row' }}
                           >
                              <Avatar
                                 size="9"
                                 radius="full"
                                 src={store.avatarPreviewImage ?? DEFAULT_AVATAR}
                                 fallback="P"
                              />

                              <Flex direction="column" gap="2">
                                 <input
                                    id={inputId}
                                    type="file"
                                    hidden
                                    accept=".jpeg,.jpg,.png"
                                    onChange={handlePhotoChange}
                                 />

                                 <Flex gap="2" wrap="wrap">
                                    <Button asChild variant="soft" color="indigo">
                                       <label htmlFor={inputId}>Обрати фото</label>
                                    </Button>

                                    <Button
                                       color="gray"
                                       variant="outline"
                                       disabled={!store.temporaryProfileImage}
                                       onClick={() => {
                                          store.resetSelectedPhoto();
                                          setPhotoError(null);
                                       }}
                                    >
                                       Скасувати
                                    </Button>
                                 </Flex>

                                 <Text size="1" color={photoError ? 'red' : 'gray'}>
                                    {photoError ??
                                       'Підтримувані типи файлів: gif, jpg, jpeg, png (макс. 10 МБ). Мінімальний розмір 250 x 250 пікселів.'}
                                 </Text>
                              </Flex>
                           </Flex>
                        </Box>

                        <form
                           onSubmit={(e) => {
                              e.preventDefault();
                              requestSubmit();
                           }}
                        >
                           <Flex
                              className="feature-edit-candidate-profile__form-grid"
                              gap="7"
                              align="start"
                           >
                              <Flex
                                 className="feature-edit-candidate-profile__main-column"
                                 direction="column"
                                 gap="4"
                              >
                                 <FormField
                                    label="Ім'я"
                                    required
                                    error={store.errors.first_name}
                                 >
                                    <TextField.Root
                                       size="3"
                                       color="indigo"
                                       placeholder="Ім'я"
                                       value={store.form.first_name}
                                       onChange={(e) =>
                                          store.setField('first_name', e.target.value)
                                       }
                                    />
                                 </FormField>

                                 <FormField
                                    label="Прізвище"
                                    required
                                    error={store.errors.last_name}
                                 >
                                    <TextField.Root
                                       size="3"
                                       color="indigo"
                                       placeholder="Прізвище"
                                       value={store.form.last_name}
                                       onChange={(e) => store.setField('last_name', e.target.value)}
                                    />
                                 </FormField>

                                 <FormField label="Стать">
                                    <RadioGroup.Root
                                       value={store.form.gender}
                                       size="3"
                                       color="indigo"
                                       variant="soft"
                                       onValueChange={(value) =>
                                          store.setGender(value as 'male' | 'female' | '')
                                       }
                                    >
                                       <Flex gap="4">
                                          <RadioGroup.Item value="female">
                                             <Text>Жіноча</Text>
                                          </RadioGroup.Item>
                                          <RadioGroup.Item value="male">
                                             <Text>Чоловіча</Text>
                                          </RadioGroup.Item>
                                       </Flex>
                                    </RadioGroup.Root>
                                 </FormField>

                                 <FormField label="Дата народження">
                                    <DateInput
                                       name="birthday"
                                       format="dd.MM.yyyy"
                                       minDate="1930-01-01"
                                       maxDate={new Date().toISOString().split('T')[0]}
                                       value={store.form.birthday}
                                       handleChange={(e) => {
                                          store.setField('birthday', e.target.value);
                                       }}
                                    />
                                 </FormField>

                                 <Heading as="h2" size="4" weight="medium" mt="4">
                                    Дані для входу
                                 </Heading>

                                 <FormField
                                    label="Електронна пошта"
                                    required
                                    error={store.errors.email_address}
                                 >
                                    <TextField.Root
                                       size="3"
                                       color="indigo"
                                       placeholder="Електронна пошта"
                                       value={store.form.email_address}
                                       onChange={(e) =>
                                          store.setField('email_address', e.target.value)
                                       }
                                    />
                                 </FormField>

                                 <FormField label="Пароль" error={store.errors.new_password}>
                                    <TextField.Root
                                       size="3"
                                       color="indigo"
                                       type="password"
                                       placeholder="Пароль"
                                       autoComplete="new-password"
                                       value={store.form.new_password}
                                       onChange={(e) =>
                                          store.setField('new_password', e.target.value)
                                       }
                                    />
                                    <Text size="1" color="gray">
                                       Якщо потрібно змінити пароль користувача, заповніть це поле.
                                       Інакше залиште його порожнім.
                                    </Text>
                                 </FormField>

                                 <FormField
                                    label="Підтвердження пароля"
                                    error={store.errors.confirm_password}
                                 >
                                    <TextField.Root
                                       size="3"
                                       color="indigo"
                                       type="password"
                                       autoComplete="new-password"
                                       placeholder="Підтвердження пароля"
                                       value={store.form.confirm_password}
                                       onChange={(e) =>
                                          store.setField('confirm_password', e.target.value)
                                       }
                                    />
                                 </FormField>

                                 <Heading as="h2" size="4" weight="medium" mt="4">
                                    Інші дані
                                 </Heading>

                                 <FormField label="Статус кандидата">
                                    <Flex align="center" gap="3">
                                       <Switch
                                          size="3"
                                          color="green"
                                          checked={store.form.status === 1}
                                          onCheckedChange={(checked) =>
                                             store.setStatus(checked ? 1 : 0)
                                          }
                                       />
                                       <Text size="2">
                                          {store.form.status === 1 ? 'Активний' : 'Неактивний'}
                                       </Text>
                                    </Flex>
                                    <Text size="1" color="gray">
                                       Зміни набудуть чинності після надсилання форми.
                                    </Text>
                                 </FormField>
                              </Flex>

                              <Flex
                                 className="feature-edit-candidate-profile__contact-column"
                                 direction="column"
                                 gap="4"
                              >
                                 <Flex direction="column" gap="4">
                                    <FormField
                                       label="Країна"
                                       required
                                       error={store.errors.country_id}
                                    >
                                       <AutocompleteV2.Root
                                          placeholder="Оберіть країну"
                                          selectedKey={store.form.country_id || null}
                                          items={countryItems}
                                          getOptionValue={(country) => country.id}
                                          getOptionLabel={(country) => country.name}
                                          onSelect={(country) => {
                                             store.onCountrySelect(country.id);
                                             store.searchCities('');
                                          }}
                                          onSearch={(value) => {
                                             setCountryQuery(value);
                                          }}
                                       />
                                    </FormField>

                                    <FormField label="Місто" required error={store.errors.city_id}>
                                       <AutocompleteV2.Root
                                          placeholder="Оберіть місто"
                                          selectedKey={store.form.city_id || null}
                                          items={store.cityOptions}
                                          loading={store.citiesLoading}
                                          disabled={!store.form.country_id}
                                          getOptionValue={(city) => city.id}
                                          getOptionLabel={(city) => city.name}
                                          onSelect={(city) => {
                                             store.onCitySelect(city.id);
                                          }}
                                          onSearch={(value) => {
                                             store.clearCitySelection();
                                             debouncedCities(value);
                                          }}
                                       />
                                    </FormField>
                                 </Flex>

                                 <FormField
                                    label="Контактний номер"
                                    error={store.errors.contact}
                                 >
                                    <TextField.Root
                                       size="3"
                                       color="indigo"
                                       placeholder="+380XXXXXXXXX"
                                       value={store.form.contact}
                                       onChange={(e) => store.setField('contact', e.target.value)}
                                    />
                                 </FormField>

                                 <Heading
                                    as="h2"
                                    size="4"
                                    weight="medium"
                                    mt="4"
                                    className="feature-edit-candidate-profile__social-heading"
                                 >
                                    Месенджери та Соцмережі
                                 </Heading>

                                 {renderMessengerRows()}

                                 {renderSocialRows()}
                              </Flex>
                           </Flex>

                           <Flex mt="6" gap="7" className="feature-edit-candidate-profile__actions">
                              <Box className="feature-edit-candidate-profile__action-column">
                                 <Button
                                    size="4"
                                    type="button"
                                    color="indigo"
                                    highContrast
                                    variant="outline"
                                    onClick={() => navigate('/admin/candidates')}
                                 >
                                    <IconWest />
                                    Назад
                                 </Button>
                              </Box>

                              <Flex
                                 className="feature-edit-candidate-profile__action-column"
                                 justify="end"
                              >
                                 <Button size="4" type="submit">
                                    Зберегти
                                 </Button>
                              </Flex>
                           </Flex>
                        </form>
                     </Box>
                  )}
               </Box>
            </Box>
         </Box>

         <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
            <Dialog.Content
               aria-describedby={undefined}
               onOpenAutoFocus={(e) => e.preventDefault()}
            >
               <Box className="feature-edit-candidate-profile__dialog">
                  <Flex justify="center">
                     <Text color="red">
                        <ErrorOutlineIcon width={80} height={80} />
                     </Text>
                  </Flex>

                  <Flex justify="center" direction="column" align="center" mt="5" mb="6">
                     <Dialog.Title aria-describedby={undefined}>
                        <Text>Оновити кандидата?</Text>
                     </Dialog.Title>
                     <Text>Ви хочете оновити цього кандидата?</Text>
                  </Flex>

                  <Flex justify="center" gap="3">
                     <Dialog.Close>
                        <Button color="gray" size="3">
                           Скасувати
                        </Button>
                     </Dialog.Close>

                     <Button
                        color="red"
                        size="3"
                        loading={store.isSaving}
                        onClick={() =>
                           store.submit({
                              onSuccess: () => {
                                 setConfirmOpen(false);
                                 toast.success('Кандидата успішно оновлено!');
                              },
                              onError: (message) => {
                                 toast.error(message);
                              },
                           })
                        }
                     >
                        Підтвердити
                     </Button>
                  </Flex>
               </Box>
            </Dialog.Content>
         </Dialog.Root>
      </>
   );
};

export const FeatureEditCandidateProfileView = observer(EditCandidateProfileView);
